import React, { useState, useEffect, useCallback, useRef, ReactElement, WheelEvent } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  IconButton,
  Skeleton,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Fade,
  Collapse,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LinkIcon from "@mui/icons-material/Link";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { Account, Currency, TransactionCategory, TransactionCategoryGroup, TransactionCustomField, CustomFieldAttachmentValue, TransactionCustomFieldScopeType } from "../../../types";
import { fieldAppliesToScope } from "../../../utils/transactionCustomFieldsDerivation";
import { formatCurrency, formatRate, getTransactionFriendlyId, truncateIdForDisplay, copyToClipboard } from "../../../utils/transactionUtils";
import { getCurrencyDecimalPlaces } from "../../../utils/currencyDefaults";
import TruncatedTextTooltip from "./TruncatedTextTooltip";
import CopyFeedbackTooltip from "../../../components/CopyFeedbackTooltip";
import { api } from "../../../services/api";

// WorkspaceMember interface
interface WorkspaceMember {
  id: string;
  name?: string | undefined;
  email?: string | undefined;
  role?: string | undefined;
}

// TransactionList props interface
interface TransactionListProps {
  // Data
  loading: boolean;
  transactions: TransactionWithDate[];
  filteredTransactions: TransactionWithDate[];
  displayedCount: number;
  hasMoreTransactions: boolean;
  loadingMore: boolean;
  shouldAnimateEmptyState: boolean;
  expandedDetails: Set<string>;
  transactionHasHistory: Set<string>;
  workspaceMembers: WorkspaceMember[];
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  isMobile: boolean;
  hasActiveFilters: boolean;
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  /** Workspace custom fields (for labels when showing customFieldValues) */
  transactionCustomFields?: TransactionCustomField[];
  /** When set, show user-friendly transaction ID column (desktop only) e.g. DV-1 */
  transactionIdPrefix?: string;
  /** Workspace ID (for secure attachment URLs) */
  workspaceId: string;
  /** Called when secure attachment URL fails (e.g. not signed in) */
  onAttachmentError?: (message: string) => void;

  // Refs
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
  
  // Handlers
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, transaction: TransactionWithDate) => void;
  onTransactionClick?: (transaction: TransactionWithDate) => void;
  onAdjustmentNotEditable?: () => void;
  setPreviewOpen: (open: boolean) => void;
  setPreviewUrl: (url: string) => void;
  setPreviewName: (name: string) => void;
  setPreviewType: (type: string | null) => void;
  setPreviewExpiresAt?: (expiresAt: number | null) => void;
  setExpandedDetails: React.Dispatch<React.SetStateAction<Set<string>>>;
  clearAllFilters: () => void;
  handleWheel: (e: WheelEvent<HTMLDivElement>) => void;
  
  // Helper functions
  getAccountName: (accountId: string | undefined, storedAccountName?: string) => string;
  getAccountById: (accountId: string | undefined) => Account | undefined;
  getAccountGroup: (accountId: string | undefined) => string;
  getUserName: (userId: string) => string;
  getUserPhotoURL: (userId: string) => string | undefined;
  getCategoryIcon: (category: string) => string;
  formatDate: (date: Date | string | null | undefined) => string;
  formatTime: (date: Date | string | null | undefined) => string;
  getAttachmentIcon: (attachmentType: string | undefined, attachmentName: string | undefined) => ReactElement;
  isProtectedAdjustment: (transaction: TransactionWithDate | null) => boolean;
  usesArchivedAccount: (transaction: TransactionWithDate) => boolean;
  getArchivedAccountNames: (transaction: TransactionWithDate) => string[];
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
}

export default function TransactionList({
  loading,
  transactions,
  filteredTransactions,
  displayedCount,
  hasMoreTransactions,
  loadingMore,
  shouldAnimateEmptyState,
  expandedDetails,
  transactionHasHistory,
  workspaceMembers,
  accounts,
  currencies,
  defaultCurrency,
  isMobile,
  hasActiveFilters,
  expenseCategories,
  incomeCategories,
  categoryGroups,
  transactionCustomFields = [],
  transactionIdPrefix,
  workspaceId,
  onAttachmentError,
  tableContainerRef,
  handleMenuOpen,
  onTransactionClick,
  onAdjustmentNotEditable,
  setPreviewOpen,
  setPreviewUrl,
  setPreviewName,
  setPreviewType,
  setPreviewExpiresAt,
  setExpandedDetails,
  clearAllFilters,
  handleWheel,
  getAccountName,
  getAccountById,
  getAccountGroup,
  getUserName,
  getUserPhotoURL,
  getCategoryIcon,
  formatDate,
  formatTime,
  getAttachmentIcon,
  isProtectedAdjustment,
  usesArchivedAccount,
  getArchivedAccountNames,
  calculateTransferRate,
}: TransactionListProps): ReactElement {
  const [expandedAdjustmentId, setExpandedAdjustmentId] = useState<string | null>(null);
  const [lastCopiedKey, setLastCopiedKey] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const showCopiedFeedback = useCallback((key: string) => {
    if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    setLastCopiedKey(key);
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setLastCopiedKey(null);
      copyFeedbackTimeoutRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    };
  }, []);

  // Same order as transaction create popup: category enabledDetails, in group details order
  const getDetailOrder = useCallback((transaction: TransactionWithDate): string[] => {
    const categoryName = transaction.category;
    const details = transaction.details || {};
    if (!categoryName) return Object.keys(details);
    const category = expenseCategories.find((c) => c.name === categoryName) ?? incomeCategories.find((c) => c.name === categoryName);
    if (!category?.groupId) return Object.keys(details);
    const group = categoryGroups.find((g) => g.id === category.groupId);
    const enabledDetailNames = Array.isArray(category.enabledDetails) ? category.enabledDetails : [];
    const ordered = (group?.details || []).filter((d) => enabledDetailNames.includes(d.name)).map((d) => d.name);
    if (ordered.length === 0) return Object.keys(details);
    const orderedSet = new Set(ordered);
    const rest = Object.keys(details).filter((k) => !orderedSet.has(k));
    return [...ordered.filter((k) => k in details), ...rest];
  }, [expenseCategories, incomeCategories, categoryGroups]);

  const getDetailUnit = useCallback((transaction: TransactionWithDate, detailName: string): string | undefined => {
    const categoryName = transaction.category;
    if (!categoryName) return undefined;
    const category = expenseCategories.find((c) => c.name === categoryName) ?? incomeCategories.find((c) => c.name === categoryName);
    if (!category?.groupId) return undefined;
    const group = categoryGroups.find((g) => g.id === category.groupId);
    const detail = group?.details?.find((d) => d.name === detailName);
    return detail?.unit;
  }, [expenseCategories, incomeCategories, categoryGroups]);

  /** All attachments for a transaction: legacy attachmentUrl + attachment-type custom fields. Dedupes by URL so the same file (stored as both legacy and in a custom field) is not shown twice. */
  const getTransactionAttachments = useCallback(
    (transaction: TransactionWithDate): { url: string; name: string; type: string; label?: string }[] => {
      const list: { url: string; name: string; type: string; label?: string }[] = [];
      const seenUrls = new Set<string>();
      const txType = transaction.type;

      const addIfNew = (url: string, name: string, type: string, label?: string) => {
        if (!url || seenUrls.has(url)) return;
        seenUrls.add(url);
        list.push({ url, name: name || "Attachment", type: type || "", label });
      };

      if (transaction.attachmentUrl) {
        addIfNew(
          transaction.attachmentUrl,
          transaction.attachmentName || "Attachment",
          transaction.attachmentType || ""
        );
      }
      transactionCustomFields
        .filter((f) => {
          if (f.type !== "attachment") return false;
          if (txType === "adjustment") return false;
          return fieldAppliesToScope(f, txType as TransactionCustomFieldScopeType);
        })
        .forEach((field) => {
          const val = transaction.customFieldValues?.[field.id];
          if (val && typeof val === "object" && "url" in val && (val as CustomFieldAttachmentValue).url) {
            const att = val as CustomFieldAttachmentValue;
            addIfNew(att.url, att.name || "Attachment", att.type || "", field.label);
          }
        });
      return list;
    },
    [transactionCustomFields]
  );

  // Use preview dialog on desktop and mobile so user can view attachments without leaving the app.
  const usePreviewDialog = true;

  /** Resolve secure signed URL and open attachment (new tab or preview). Open new tab synchronously on click so popup blockers allow it (desktop and mobile). */
  const openAttachment = useCallback(
    async (
      url: string,
      options?: { forPreview?: boolean; name?: string; type?: string }
    ) => {
      let newWindow: Window | null = null;
      if (!options?.forPreview) {
        newWindow = window.open("", "_blank");
      }
      try {
        const { url: signedUrl, expiresIn } = await api.getAttachmentSignedUrl(workspaceId, url);
        if (options?.forPreview) {
          setPreviewUrl(signedUrl);
          setPreviewName(options.name ?? "Attachment");
          setPreviewType(options.type ?? null);
          setPreviewExpiresAt?.(Date.now() + expiresIn * 1000);
          setPreviewOpen(true);
        } else if (newWindow) {
          newWindow.location.href = signedUrl;
        }
      } catch (e) {
        if (newWindow) newWindow.close();
        onAttachmentError?.(e instanceof Error ? e.message : "Could not open attachment");
      }
    },
    [workspaceId, onAttachmentError, setPreviewUrl, setPreviewName, setPreviewType, setPreviewOpen, setPreviewExpiresAt]
  );

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: isMobile ? 0 : 2, minHeight: 0, width: isMobile ? "100%" : "calc(100% + 20px)", maxWidth: isMobile ? "100%" : "calc(100% + 20px)", mt: isMobile ? "-10px" : "-20px", ml: isMobile ? 0 : "-10px" }}>
      {loading ? (
        <TableContainer 
          component={Paper} 
          elevation={0} 
          sx={{ 
            border: 1, 
            borderColor: "divider",
            flex: 1,
            overflow: "auto",
            mt: isMobile ? 0 : "-5px",
            bgcolor: "background.default",
            "& .MuiPaper-root": {
              bgcolor: "background.default"
            }
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                {!isMobile && (
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 72, maxWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>ID</TableCell>
                )}
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", maxWidth: 120, width: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 200, maxWidth: 300, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Description</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Group</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 180, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Account</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>User</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 88, maxWidth: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Attachments</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>More</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {!isMobile && <TableCell><Skeleton width={48} /></TableCell>}
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={150} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={120} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell align="center"><Skeleton width={40} /></TableCell>
                  <TableCell align="right"><Skeleton width={80} /></TableCell>
                  <TableCell align="right"><Skeleton width={40} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : !loading && transactions.length === 0 ? (
        // Empty state when user has no transactions yet
        isMobile ? (
          // Mobile: Card Layout Empty State
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              px: 1,
              py: 0.75,
              minHeight: 0,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: "100%",
                border: 1,
                borderColor: "divider",
                borderRadius: 0.75,
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 3, textAlign: "center" }}>
                <Fade in={shouldAnimateEmptyState} timeout={800}>
                  <Stack spacing={1.5} alignItems="center">
                    <ReceiptIcon sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5 }} />
                    <Typography variant="h6" color="text.secondary">
                      No transactions yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                      Use the buttons below to add your first transaction
                    </Typography>
                  </Stack>
                </Fade>
              </CardContent>
            </Card>
          </Box>
        ) : (
          // Desktop: Table Layout Empty State
          <TableContainer 
            component={Paper} 
            elevation={0} 
            sx={{ 
              border: 1, 
              borderColor: "divider",
              flex: 1,
              overflow: "auto",
              mx: "10px",
              mt: isMobile ? 0 : "-5px",
              bgcolor: "transparent",
              "& .MuiPaper-root": {
                bgcolor: "transparent"
              }
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: "background.default" }}>
                  {!isMobile && (
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 72, maxWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>ID</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", maxWidth: 120, width: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 200, maxWidth: 300, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Group</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 180, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>User</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 88, maxWidth: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Attachments</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>More</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={isMobile ? 10 : 11} sx={{ textAlign: "center", py: 8 }}>
                    <Fade in={shouldAnimateEmptyState} timeout={800}>
                      <Stack spacing={1} alignItems="center">
                        <ReceiptIcon sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary">
                          No transactions yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Use the buttons below to add your first transaction
                        </Typography>
                      </Stack>
                    </Fade>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : filteredTransactions.length === 0 && transactions.length > 0 ? (
        // Empty state when filters return no results
        isMobile ? (
          // Mobile: Card Layout Filtered Empty State
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              px: 1,
              py: 0.75,
              minHeight: 0,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: "100%",
                border: 1,
                borderColor: "divider",
                borderRadius: 0.75,
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 3, textAlign: "center" }}>
                <Stack spacing={1.5} alignItems="center">
                  <Typography variant="h6" color="text.secondary">
                    No transactions found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                    Try adjusting your filters to see more results
                  </Typography>
                  {hasActiveFilters && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearAllFilters}
                      sx={{ mt: 1 }}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        ) : (
          // Desktop: Table Layout Filtered Empty State
          <TableContainer 
            component={Paper} 
            elevation={0} 
            sx={{ 
              border: 1, 
              borderColor: "divider",
              flex: 1,
              overflow: "auto",
              mx: "10px",
              mt: isMobile ? 0 : "-5px",
              bgcolor: "transparent",
              "& .MuiPaper-root": {
                bgcolor: "transparent"
              }
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: "background.default" }}>
                  {!isMobile && (
                    <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 72, maxWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>ID</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", maxWidth: 120, width: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 200, maxWidth: 300, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Group</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 180, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>User</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 88, maxWidth: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Attachments</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>More</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={isMobile ? 10 : 11} sx={{ textAlign: "center", py: 6 }}>
                    <Stack spacing={1.5} alignItems="center">
                      <Typography variant="h6" color="text.secondary">
                        No transactions found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your filters to see more results
                      </Typography>
                      {hasActiveFilters && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={clearAllFilters}
                          sx={{ mt: 1 }}
                        >
                          Clear All Filters
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : isMobile ? (
        // Mobile: Card Layout - Continue in next part due to size
        <Box
          ref={tableContainerRef}
          onWheel={handleWheel}
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            px: 1,
            py: 0.75,
            minHeight: 0,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            position: "relative",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            willChange: "scroll-position",
            touchAction: "pan-y pinch-zoom",
            cursor: "default",
            "& > *": {
              pointerEvents: "auto",
            },
            "& *": {
              pointerEvents: "auto",
            },
          }}
        >
          <Stack 
            spacing={0.5} 
            sx={{ 
              width: "100%", 
              maxWidth: "100%", 
              boxSizing: "border-box",
              pointerEvents: "auto",
            }}
          >
            {filteredTransactions.slice(0, displayedCount).map((transaction, index) => {
              // Find related transactions
              const isTransferFeeTransaction = transaction.type === "expense" &&
                ((transaction.title || "") === "Transfer Fee" || (transaction.category || "") === "Transfer Fee") &&
                transaction.transferFeeFor;
              const isExpenseFeeTransaction = transaction.type === "expense" &&
                ((transaction.title || "") === "Expense Fee" || (transaction.category || "") === "Expense Fee") &&
                (transaction as any).expenseFeeFor;
              const isFeeTransaction = isTransferFeeTransaction || isExpenseFeeTransaction;

              const relatedTransfer = isTransferFeeTransaction && transaction.transferFeeFor
                ? transactions.find(t => t.id === transaction.transferFeeFor && t.type === "transfer")
                : null;
              const relatedExpense = isExpenseFeeTransaction && (transaction as any).expenseFeeFor
                ? transactions.find(t => t.id === (transaction as any).expenseFeeFor && (t.type === "expense" || t.type === "income"))
                : null;

              const relatedFee = transaction.type === "transfer"
                ? transactions.find(t =>
                    t.type === "expense" &&
                    ((t.title || "") === "Transfer Fee" || (t.category || "") === "Transfer Fee") &&
                    t.transferFeeFor === transaction.id
                  )
                : transaction.type === "expense" && !isFeeTransaction
                  ? transactions.find(t =>
                      t.type === "expense" &&
                      ((t.title || "") === "Expense Fee" || (t.category || "") === "Expense Fee") &&
                      (t as any).expenseFeeFor === transaction.id
                    )
                  : null;

              // Type not shown on left – income/expense implied by amount color; adjustment shown under amount

              const getDescription = () => {
                if (transaction.type === "adjustment") {
                  if (transaction.isInitialBalance) {
                    return "Initial Balance";
                  } else {
                    const account = getAccountById(transaction.accountId);
                    const accountCurrency = account?.currency || defaultCurrency;
                    const adjustmentAmount = transaction.amount || 0;
                    const previousAmount = transaction.previousAmount;
                    
                    if (previousAmount !== undefined && previousAmount !== null) {
                      const newAmount = previousAmount + adjustmentAmount;
                      const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
                      const formattedPrevious = formatCurrency(previousAmount, accountCurrency, accountDecimalPlaces);
                      const formattedCurrent = formatCurrency(newAmount, accountCurrency, accountDecimalPlaces);
                      return `Balance changed from ${formattedPrevious} to ${formattedCurrent}`;
                    } else {
                      const formattedAmount = formatCurrency(adjustmentAmount, accountCurrency);
                      const direction = adjustmentAmount >= 0 ? "increased" : "decreased";
                      return `Balance ${direction} to ${formattedAmount}`;
                    }
                  }
                } else if (transaction.type === "transfer") {
                  return transaction.note || "-";
                } else {
                  return transaction.note || "";
                }
              };

              const getCategoryDisplay = () => {
                if (transaction.type === "transfer" || transaction.type === "adjustment") {
                  return "-";
                }
                return transaction.category || "-";
              };

              const getAmountDisplay = () => {
                if (transaction.type === "adjustment") {
                  const absAmount = Math.abs(transaction.amount || 0);
                  const sign = transaction.amount >= 0 ? "+" : "-";
                  const adjustmentDecimalPlaces = transaction.currency
                    ? getCurrencyDecimalPlaces(transaction.currency, currencies, defaultCurrency)
                    : getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                  const formatted = transaction.currency
                    ? formatCurrency(absAmount, transaction.currency, adjustmentDecimalPlaces)
                    : (() => {
                        const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                        return formatCurrency(absAmount, defaultCurrency, defaultDecimalPlaces);
                      })();
                  return (
                    <Typography variant="body1" fontWeight="bold" color={transaction.amount >= 0 ? "success.main" : "error.main"} sx={{ fontSize: "0.9rem" }}>
                      {sign}{formatted}
                    </Typography>
                  );
                } else if (transaction.type === "transfer") {
                  const fromAccount = getAccountById(transaction.fromAccountId);
                  const toAccount = getAccountById(transaction.toAccountId);
                  const currenciesDiffer = fromAccount && toAccount && fromAccount.currency !== toAccount.currency;
                  
                  if (currenciesDiffer && transaction.transferRate) {
                    const convertedAmount = (transaction.amount || 0) * transaction.transferRate;
                    const fromDecimalPlaces = getCurrencyDecimalPlaces(fromAccount?.currency || "", currencies, defaultCurrency);
                    const toDecimalPlaces = getCurrencyDecimalPlaces(toAccount?.currency || "", currencies, defaultCurrency);
                    return (
                      <Box>
                        <Typography variant="body1" fontWeight="bold" color="text.primary" sx={{ fontSize: "0.9rem" }}>
                          {formatCurrency(transaction.amount || 0, fromAccount?.currency || "", fromDecimalPlaces)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          {formatCurrency(convertedAmount, toAccount?.currency || "", toDecimalPlaces)}
                        </Typography>
                      </Box>
                    );
                  }
                  
                  const transferCurrency = transaction.currency || defaultCurrency;
                  const transferDecimalPlaces = getCurrencyDecimalPlaces(transferCurrency, currencies, defaultCurrency);
                  return (
                    <Typography variant="body1" fontWeight="bold" color="text.primary" sx={{ fontSize: "0.9rem" }}>
                      {transaction.currency
                        ? formatCurrency(transaction.amount || 0, transaction.currency, transferDecimalPlaces)
                        : (() => {
                            const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                            return formatCurrency(transaction.amount || 0, defaultCurrency, defaultDecimalPlaces);
                          })()}
                    </Typography>
                  );
                } else {
                  const transactionCurrency = transaction.currency || defaultCurrency;
                  const currenciesDiffer = transactionCurrency !== defaultCurrency;
                  
                  const transactionDecimalPlaces = transaction.currency 
                    ? getCurrencyDecimalPlaces(transaction.currency, currencies, defaultCurrency)
                    : getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                  const formatted = transaction.currency
                    ? formatCurrency(transaction.amount || 0, transaction.currency, transactionDecimalPlaces)
                    : (() => {
                        const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                        return formatCurrency(transaction.amount || 0, defaultCurrency, defaultDecimalPlaces);
                      })();
                  
                  if (currenciesDiffer && transaction.amount) {
                    let amountInDefaultCurrency: number;
                    const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                    const storedRateRaw = transaction.defaultCurrencyRate;
                    const storedRateNum = storedRateRaw !== undefined && storedRateRaw !== null
                      ? (typeof storedRateRaw === "number" ? storedRateRaw : parseFloat(String(storedRateRaw)))
                      : NaN;
                    const hasStoredRate = !isNaN(storedRateNum) && storedRateNum > 0;
                    if (hasStoredRate) {
                      amountInDefaultCurrency = (transaction.amount || 0) * storedRateNum;
                    } else if (transaction.amountInDefaultCurrency !== undefined && 
                        transaction.defaultCurrencyAtTime === defaultCurrency) {
                      const storedAmount = transaction.amountInDefaultCurrency;
                        // No stored rate - validate stored amount against current settings
                        // Calculate expected amount using current rates for validation
                        const expectedRateToDefault = calculateTransferRate(transactionCurrency, defaultCurrency);
                        const expectedAmountInDefault = (transaction.amount || 0) * expectedRateToDefault;
                        
                        
                        // Validate: If stored value is way off (more than 3x different), it's likely wrong
                        // This catches cases where mobile sent wrong defaultCurrencyRate (e.g., 167,084 instead of 444.4)
                        // Also catch cases where storedAmount equals transactionAmount but currencies differ (should be different)
                        // Only validate if expected amount is reasonable (not zero or very small)
                        if (expectedAmountInDefault > 0.01) {
                          const ratio = storedAmount / expectedAmountInDefault;
                          const isWayOff = ratio > 3 || ratio < 0.33;
                          // If currencies differ, storedAmount should NOT equal transactionAmount (unless rate is exactly 1, which is rare)
                          const currenciesMatchButAmountsEqual = storedAmount === transaction.amount && expectedAmountInDefault !== transaction.amount;
                          
                          if (isWayOff || currenciesMatchButAmountsEqual) {
                            // Stored value seems wrong - use current rate calculation
                            amountInDefaultCurrency = expectedAmountInDefault;
                          } else {
                            // Stored value seems reasonable, use it
                            amountInDefaultCurrency = storedAmount;
                          }
                        } else {
                          // Expected amount is too small to validate reliably, use stored value
                          amountInDefaultCurrency = storedAmount;
                        }
                    } else if (transaction.defaultCurrencyAtTime && transaction.defaultCurrencyAtTime !== defaultCurrency) {
                      // Default currency changed - recalculate from transaction amount × current rate (so 1000 AMD × rate = correct default-currency amount)
                      const rateToDefault = calculateTransferRate(transactionCurrency, defaultCurrency);
                      amountInDefaultCurrency = (transaction.amount || 0) * rateToDefault;
                    } else {
                      // No historical data - calculate using current rates
                      const rateToDefault = calculateTransferRate(transactionCurrency, defaultCurrency);
                      amountInDefaultCurrency = (transaction.amount || 0) * rateToDefault;
                    }
                    
                    return (
                      <Box>
                        <Typography variant="body1" fontWeight="bold" color={transaction.isIncome ? "success.main" : "error.main"} sx={{ fontSize: "0.9rem" }}>
                          {transaction.isIncome ? "+" : "-"}{formatted}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          {formatCurrency(amountInDefaultCurrency, defaultCurrency, defaultDecimalPlaces)}
                        </Typography>
                      </Box>
                    );
                  }
                  
                  return (
                    <Typography variant="body1" fontWeight="bold" color={transaction.isIncome ? "success.main" : "error.main"} sx={{ fontSize: "0.9rem" }}>
                      {transaction.isIncome ? "+" : "-"}{formatted}
                    </Typography>
                  );
                }
              };

              return (
                <Box key={`${transaction.id}-${index}`} data-transaction-id={transaction.id} sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", mb: 0, pb: 0 }}>
                  <Card 
                    elevation={1}
                    data-card-id={transaction.id}
                    onClick={
                      isMobile && (onTransactionClick || onAdjustmentNotEditable)
                        ? (e) => {
                            if ((e.target as HTMLElement).closest("button") || (e.target as HTMLElement).closest("[role='button']")) return;
                            if (isProtectedAdjustment(transaction)) {
                              onAdjustmentNotEditable?.();
                              return;
                            }
                            onTransactionClick?.(transaction);
                          }
                        : undefined
                    }
                    onTouchStart={
                      isMobile && (onTransactionClick || onAdjustmentNotEditable)
                        ? (e) => {
                            touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                          }
                        : undefined
                    }
                    onTouchEnd={
                      isMobile && (onTransactionClick || onAdjustmentNotEditable)
                        ? (e) => {
                            if ((e.target as HTMLElement).closest("button") || (e.target as HTMLElement).closest("[role='button']")) return;
                            const end = e.changedTouches[0];
                            const dx = end.clientX - touchStartRef.current.x;
                            const dy = end.clientY - touchStartRef.current.y;
                            const distSq = dx * dx + dy * dy;
                            if (distSq < 100) {
                              e.preventDefault();
                              if (isProtectedAdjustment(transaction)) {
                                onAdjustmentNotEditable?.();
                                return;
                              }
                              onTransactionClick?.(transaction);
                            }
                          }
                        : undefined
                    }
                    sx={{
                      width: "100%",
                      border: "none",
                      borderRadius: 0.75,
                      overflow: "hidden",
                      bgcolor: "background.paper",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                      touchAction: "manipulation",
                      ...(isProtectedAdjustment(transaction)
                        ? {
                            opacity: 0.85,
                            cursor: "default",
                            "&:hover": {},
                          }
                        : {
                            cursor: isMobile && onTransactionClick ? "pointer" : "default",
                            "&:hover": { 
                              bgcolor: "action.hover",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
                              transform: "translateY(-1px)",
                            },
                          }),
                      pointerEvents: "auto",
                      transition: "all 0.2s ease-in-out, background-color 0.5s ease-in-out",
                    }}
                  >
                    <CardContent 
                      sx={{ 
                        pt: "6px", 
                        px: "8px", 
                        pb: "2px !important",
                        "&.MuiCardContent-root": {
                          paddingBottom: "2px !important",
                        },
                        "&:last-child": { 
                          pb: "2px !important",
                          paddingBottom: "2px !important",
                        }
                      }}
                    >
                      {/* Header: Date, User initial, Category | Amount, Adjustment */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.15 }}>
                        <Stack spacing={0.15} sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                              {formatDate(transaction.date)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7, fontSize: "0.65rem" }}>
                              {formatTime(transaction.date)}
                            </Typography>
                            {transaction.userId && (
                              <Tooltip title={`Added by ${getUserName(transaction.userId)}`}>
                                <Avatar
                                  src={getUserPhotoURL(transaction.userId) || undefined}
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    bgcolor: "primary.main",
                                    fontSize: "0.5rem",
                                  }}
                                >
                                  {getUserName(transaction.userId)[0]?.toUpperCase() || "?"}
                                </Avatar>
                              </Tooltip>
                            )}
                            {/* Relation icons after avatar (like edit icon) */}
                            {isFeeTransaction && relatedTransfer && (
                              <Tooltip title={`Fee for transfer: ${formatCurrency(relatedTransfer.amount || 0, relatedTransfer.currency || defaultCurrency)}`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const transferElement = document.querySelector(`[data-transaction-id="${relatedTransfer.id}"]`) as HTMLElement;
                                    if (transferElement) {
                                      transferElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                      setTimeout(() => {
                                        let cardElement = transferElement.querySelector(`[data-card-id="${relatedTransfer.id}"]`) as HTMLElement;
                                        if (!cardElement && transferElement.firstElementChild) {
                                          cardElement = transferElement.firstElementChild as HTMLElement;
                                        }
                                        if (!cardElement) {
                                          cardElement = transferElement.querySelector("div[class*='MuiCard-root']") as HTMLElement;
                                        }
                                        if (cardElement) {
                                          cardElement.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                          cardElement.style.transition = "background-color 0.5s ease-in-out";
                                          const cardContent = cardElement.querySelector(".MuiCardContent-root") as HTMLElement;
                                          if (cardContent) {
                                            cardContent.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                            cardContent.style.transition = "background-color 0.5s ease-in-out";
                                          }
                                          setTimeout(() => {
                                            cardElement.style.transition = "background-color 1s ease-in-out";
                                            cardElement.style.setProperty("background-color", "", "important");
                                            if (cardContent) {
                                              cardContent.style.transition = "background-color 1s ease-in-out";
                                              cardContent.style.setProperty("background-color", "", "important");
                                            }
                                          }, 2500);
                                        }
                                      }, 100);
                                    }
                                  }}
                                  sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                >
                                  <LinkIcon sx={{ fontSize: 12, color: "primary.main" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isFeeTransaction && relatedExpense && (
                              <Tooltip title={`Fee for expense: ${(relatedExpense.title || relatedExpense.category || "").slice(0, 30)}`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const expenseElement = document.querySelector(`[data-transaction-id="${relatedExpense.id}"]`) as HTMLElement;
                                    if (expenseElement) {
                                      expenseElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                      setTimeout(() => {
                                        let cardElement = expenseElement.querySelector(`[data-card-id="${relatedExpense.id}"]`) as HTMLElement;
                                        if (!cardElement && expenseElement.firstElementChild) cardElement = expenseElement.firstElementChild as HTMLElement;
                                        if (!cardElement) cardElement = expenseElement.querySelector("div[class*='MuiCard-root']") as HTMLElement;
                                        if (cardElement) {
                                          cardElement.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                          cardElement.style.transition = "background-color 0.5s ease-in-out";
                                          setTimeout(() => {
                                            cardElement.style.transition = "background-color 1s ease-in-out";
                                            cardElement.style.setProperty("background-color", "", "important");
                                          }, 2500);
                                        }
                                      }, 100);
                                    }
                                  }}
                                  sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                >
                                  <LinkIcon sx={{ fontSize: 12, color: "primary.main" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(transaction.type === "transfer" || transaction.type === "expense") && relatedFee && (
                              <Tooltip title={`Has fee: ${formatCurrency(relatedFee.amount || 0, relatedFee.currency || defaultCurrency)}`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const feeElement = document.querySelector(`[data-transaction-id="${relatedFee.id}"]`) as HTMLElement;
                                    if (feeElement) {
                                      feeElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                      setTimeout(() => {
                                        let cardElement = feeElement.querySelector(`[data-card-id="${relatedFee.id}"]`) as HTMLElement;
                                        if (!cardElement && feeElement.firstElementChild) {
                                          cardElement = feeElement.firstElementChild as HTMLElement;
                                        }
                                        if (!cardElement) {
                                          cardElement = feeElement.querySelector("div[class*='MuiCard-root']") as HTMLElement;
                                        }
                                        if (cardElement) {
                                          cardElement.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                          cardElement.style.transition = "background-color 0.5s ease-in-out";
                                          const cardContent = cardElement.querySelector(".MuiCardContent-root") as HTMLElement;
                                          if (cardContent) {
                                            cardContent.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                            cardContent.style.transition = "background-color 0.5s ease-in-out";
                                          }
                                          setTimeout(() => {
                                            cardElement.style.transition = "background-color 1s ease-in-out";
                                            cardElement.style.setProperty("background-color", "", "important");
                                            if (cardContent) {
                                              cardContent.style.transition = "background-color 1s ease-in-out";
                                              cardContent.style.setProperty("background-color", "", "important");
                                            }
                                          }, 2500);
                                        }
                                      }, 100);
                                    }
                                  }}
                                  sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                >
                                  <AccountBalanceWalletIcon sx={{ fontSize: 12, color: "warning.main" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Pencil (edited) after avatar on mobile */}
                            <Box component="span" sx={{ display: "inline-flex", minWidth: 14, justifyContent: "center" }}>
                              {transactionHasHistory.has(transaction.id) && (
                                <Tooltip title="This transaction has been edited">
                                  <EditIcon sx={{ fontSize: 11, color: "primary.main" }} />
                                </Tooltip>
                              )}
                            </Box>
                          </Stack>
                          {(getCategoryDisplay() !== "-" || transaction.type === "adjustment") && (
                            transaction.type === "adjustment" ? (
                              <Tooltip title={expandedAdjustmentId === transaction.id ? "Tap to collapse" : "Tap to see full text"}>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  fontWeight="medium"
                                  color="text.primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAdjustmentId((prev) => (prev === transaction.id ? null : transaction.id));
                                  }}
                                  sx={{
                                    fontSize: "0.7rem",
                                    lineHeight: 1.2,
                                    minWidth: 0,
                                    cursor: "pointer",
                                    ...(expandedAdjustmentId === transaction.id
                                      ? { wordBreak: "break-word" }
                                      : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
                                  }}
                                >
                                  {getDescription()}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" fontWeight="medium" color="text.primary" sx={{ fontSize: "0.7rem", lineHeight: 1.2, wordBreak: "break-word" }}>
                                {getCategoryDisplay()}
                              </Typography>
                            )
                          )}
                        </Stack>
                        <Stack spacing={0.15} alignItems="flex-end" sx={{ ml: 0.5, flexShrink: 0, minWidth: "5rem", "& > *": { textAlign: "right" } }}>
                          <Box sx={{ width: "100%" }}>{getAmountDisplay()}</Box>
                        </Stack>
                      </Stack>

                      {/* Description/Title – only for non-adjustment (note/transfer note) so adjustment cards stay same height */}
                      {getDescription() && transaction.type !== "adjustment" && (
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.15, wordBreak: "break-word", fontSize: "0.75rem", lineHeight: 1.25 }}>
                          {getDescription()}
                        </Typography>
                      )}

                      {/* Account line only */}
                      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ justifyContent: "space-between", mb: 0.05 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}>
                              {(() => {
                                if (transaction.type === "transfer") {
                                  const fromAccount = getAccountById(transaction.fromAccountId);
                                  const toAccount = getAccountById(transaction.toAccountId);
                                  return `${getAccountName(transaction.fromAccountId, transaction.fromAccountName)} → ${getAccountName(transaction.toAccountId, transaction.toAccountName)}`;
                                } else {
                                  return getAccountName(transaction.accountId, transaction.accountName);
                                }
                              })()}
                            </Typography>
                          </Box>
                          <Stack direction="row" alignItems="center" spacing={0.25}>
                            {/* Expand arrow on right of account (show when details, custom fields, or friendly ID) */}
                            {(() => {
                              const hasDetails = transaction.details && Object.keys(transaction.details).length > 0;
                              const hasFriendlyId = !!getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence);
                              const canExpand = hasDetails || hasFriendlyId || !!transaction.id;
                              return (
                            <Tooltip title={canExpand ? "Show details" : "No details available"}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedDetails((prev) => {
                                      const newSet = new Set(prev);
                                      if (newSet.has(transaction.id)) {
                                        newSet.delete(transaction.id);
                                      } else {
                                        newSet.add(transaction.id);
                                      }
                                      return newSet;
                                    });
                                  }}
                                  disabled={!canExpand}
                                  sx={{ p: 0.25 }}
                                >
                                  {expandedDetails.has(transaction.id) ? (
                                    <ExpandMoreIcon sx={{ fontSize: 14 }} />
                                  ) : (
                                    <ChevronRightIcon sx={{ fontSize: 14 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                              );
                            })()}
                            {/* Menu button on right */}
                            <Tooltip
                              title={
                                isProtectedAdjustment(transaction)
                                  ? "This adjustment transaction cannot be edited"
                                  : usesArchivedAccount(transaction)
                                  ? `The account "${getArchivedAccountNames(transaction).join('", "')}" used for this transaction is currently archived, so this transaction cannot be edited.`
                                  : ""
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMenuOpen(e, transaction);
                                }}
                                disabled={isProtectedAdjustment(transaction) || usesArchivedAccount(transaction)}
                                sx={{ 
                                  p: 0.25,
                                  zIndex: 1,
                                  position: "relative",
                                }}
                              >
                                <MoreVertIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                      </Stack>

                      {/* Attachments - visible without expanding; tap to preview */}
                      {isMobile && getTransactionAttachments(transaction).length > 0 && (
                        <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={0.5} sx={{ mt: 0.35, mb: 0.05 }} onClick={(e) => e.stopPropagation()}>
                          {getTransactionAttachments(transaction).map((att, i) => (
                            <Tooltip key={`${att.url}-${i}`} title={att.name || "View attachment"}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAttachment(att.url, { forPreview: true, name: att.name, type: att.type });
                                }}
                                sx={{ p: 0.25 }}
                                color="primary"
                              >
                                {getAttachmentIcon(att.type, att.name)}
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Stack>
                      )}

                      {/* Transaction ID - when expanded (mobile); show friendly ID or document id (e.g. fee transactions); long IDs truncated with ellipsis */}
                      {isMobile && expandedDetails.has(transaction.id) && (getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence) || transaction.id) && (
                        <Box sx={{ mt: 0.25, pt: 0.25, pb: 0.25, bgcolor: "action.hover", borderRadius: 0.75, px: 0.5 }} onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.25 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24, fontSize: "0.65rem" }}>
                              ID:
                            </Typography>
                            <Tooltip title={(getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence) ?? transaction.id) || ""}>
                              <Typography variant="caption" sx={{ flex: 1, fontSize: "0.65rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                                {truncateIdForDisplay(getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence) ?? transaction.id ?? undefined)}
                              </Typography>
                            </Tooltip>
                            <CopyFeedbackTooltip
                              copyLabel="Copy ID"
                              feedbackKey={`mobile-id-${transaction.id}`}
                              lastCopiedKey={lastCopiedKey}
                              isMobile={true}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const id = getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence) ?? transaction.id;
                                  if (id) copyToClipboard(id).then((ok) => ok && showCopiedFeedback(`mobile-id-${transaction.id}`));
                                }}
                                sx={{ p: 0.25, flexShrink: 0 }}
                                color="inherit"
                                aria-label="Copy transaction ID"
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </CopyFeedbackTooltip>
                          </Stack>
                        </Box>
                      )}

                      {/* Transaction Details - Expandable (only shows when arrow is clicked); stopPropagation so tap doesn't open edit */}
                      {transaction.details && Object.keys(transaction.details).length > 0 && expandedDetails.has(transaction.id) && (
                        <Box sx={{ mt: 0.25, pt: 0.25, pb: 0.25, bgcolor: "action.hover", borderRadius: 0.75, px: 0.5 }} onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: "block", fontSize: "0.65rem" }}>
                            Details:
                          </Typography>
                          <Stack spacing={0.5}>
                            {getDetailOrder(transaction).map((key) => {
                              const value = transaction.details![key];
                              if (value === undefined) return null;
                              const unit = getDetailUnit(transaction, key);
                              const displayValue = typeof value === "number" ? value.toString() : value || "-";
                              return (
                                <Stack key={key} direction="row" spacing={0.5} alignItems="flex-start" sx={{ py: 0.25 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, fontSize: "0.65rem" }}>
                                    {key}:
                                  </Typography>
                                  <Typography variant="caption" sx={{ flex: 1, fontSize: "0.65rem" }}>
                                    {displayValue}{unit ? ` ${unit}` : ""}
                                  </Typography>
                                </Stack>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}

                      {/* Custom field values - when expanded; stopPropagation so tap doesn't open edit */}
                      {transaction.customFieldValues && Object.keys(transaction.customFieldValues).length > 0 && expandedDetails.has(transaction.id) && transactionCustomFields.length > 0 && (
                        <Box sx={{ mt: 0.25, pt: 0.25, pb: 0.25, bgcolor: "action.hover", borderRadius: 0.75, px: 0.5 }} onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: "block", fontSize: "0.65rem" }}>
                            Custom fields:
                          </Typography>
                          <Stack spacing={0.5}>
                            {transactionCustomFields
                              .filter((f) => transaction.customFieldValues?.[f.id] != null)
                              .map((field) => {
                              const value = transaction.customFieldValues![field.id];
                              const label = field.label;
                              const isAttachment = value && typeof value === "object" && "url" in value;
                              const attachmentVal = isAttachment ? (value as CustomFieldAttachmentValue) : null;
                              const displayValue = typeof value === "string" ? value : (value && typeof value === "object" && "name" in value ? (value as { name?: string }).name : "-");
                              if (displayValue === undefined || displayValue === "") return null;
                              const textToCopy = typeof displayValue === "string" ? displayValue : "";
                              return (
                                <Stack key={field.id} direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.25 }}>
                                  {attachmentVal?.url ? (
                                    <Tooltip title="Preview">
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openAttachment(attachmentVal.url, usePreviewDialog ? { forPreview: true, name: attachmentVal.name, type: attachmentVal.type } : undefined); }} sx={{ p: 0.25, flexShrink: 0 }} color="primary">
                                        <DownloadIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <CopyFeedbackTooltip
                                      copyLabel="Copy"
                                      feedbackKey={`mobile-field-${transaction.id}-${field.id}`}
                                      lastCopiedKey={lastCopiedKey}
                                      isMobile={true}
                                    >
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyToClipboard(textToCopy).then((ok) => ok && showCopiedFeedback(`mobile-field-${transaction.id}-${field.id}`)); }} sx={{ p: 0.25, flexShrink: 0 }} color="inherit">
                                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </CopyFeedbackTooltip>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, flexShrink: 0, fontSize: "0.65rem" }}>
                                    {label}:
                                  </Typography>
                                  {attachmentVal?.url ? (
                                    <>
                                      <Tooltip title={attachmentVal.name || (usePreviewDialog ? "Preview" : "Open")}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openAttachment(attachmentVal.url, usePreviewDialog ? { forPreview: true, name: attachmentVal.name, type: attachmentVal.type } : undefined);
                                          }}
                                          sx={{ p: 0.25, flexShrink: 0 }}
                                          color="primary"
                                        >
                                          {getAttachmentIcon(attachmentVal.type, attachmentVal.name)}
                                        </IconButton>
                                      </Tooltip>
                                      <Typography
                                        variant="caption"
                                        sx={{ flex: 1, minWidth: 0, fontSize: "0.65rem", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openAttachment(attachmentVal.url, usePreviewDialog ? { forPreview: true, name: attachmentVal.name, type: attachmentVal.type } : undefined);
                                        }}
                                      >
                                        {attachmentVal.name}
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography variant="caption" sx={{ flex: 1, minWidth: 0, fontSize: "0.65rem" }}>
                                      {displayValue}
                                    </Typography>
                                  )}
                                </Stack>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Stack>
          {/* Pagination info for mobile */}
          {displayedCount < filteredTransactions.length && (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Showing {displayedCount} of {filteredTransactions.length} transactions
              </Typography>
            </Box>
          )}
          {displayedCount >= filteredTransactions.length && hasMoreTransactions && (
            <Box sx={{ py: 2, textAlign: "center" }}>
              {loadingMore ? (
                <CircularProgress size={20} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Scroll to load more transactions...
                </Typography>
              )}
            </Box>
          )}
          {displayedCount >= filteredTransactions.length && !hasMoreTransactions && filteredTransactions.length > 0 && (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                You've seen all {filteredTransactions.length} transactions
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Desktop: Table Layout - Continue in next part due to size
        <TableContainer 
          ref={tableContainerRef}
          component={Paper} 
          elevation={0} 
          sx={{ 
            border: 1, 
            borderColor: "divider",
            flex: 1,
            overflow: "auto",
            mt: isMobile ? 0 : "-5px",
            bgcolor: "background.default",
            "& .MuiPaper-root": {
              bgcolor: "background.default"
            }
          }}
        >
          <Table 
            stickyHeader
            sx={{
              borderCollapse: "separate",
              borderSpacing: "0 8px",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                {!isMobile && (
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 72, maxWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>ID</TableCell>
                )}
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", maxWidth: 120, width: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 200, maxWidth: 300, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Description</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 100, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Group</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 180, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Account</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>User</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "background.default", minWidth: 88, maxWidth: 120, borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Attachments</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "background.default", borderBottom: "2px solid", borderColor: "divider", py: 1.5, color: "text.primary" }}>More</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.slice(0, displayedCount).map((transaction, index) => {
                // Find related transactions
                const isTransferFeeTransaction = transaction.type === "expense" &&
                  ((transaction.title || "") === "Transfer Fee" || (transaction.category || "") === "Transfer Fee") &&
                  transaction.transferFeeFor;
                const isExpenseFeeTransaction = transaction.type === "expense" &&
                  ((transaction.title || "") === "Expense Fee" || (transaction.category || "") === "Expense Fee") &&
                  (transaction as any).expenseFeeFor;
                const isFeeTransaction = isTransferFeeTransaction || isExpenseFeeTransaction;

                const relatedTransfer = isTransferFeeTransaction && transaction.transferFeeFor
                  ? transactions.find(t => t.id === transaction.transferFeeFor && t.type === "transfer")
                  : null;
                const relatedExpense = isExpenseFeeTransaction && (transaction as any).expenseFeeFor
                  ? transactions.find(t => t.id === (transaction as any).expenseFeeFor && (t.type === "expense" || t.type === "income"))
                  : null;

                const relatedFee = transaction.type === "transfer"
                  ? transactions.find(t =>
                      t.type === "expense" &&
                      ((t.title || "") === "Transfer Fee" || (t.category || "") === "Transfer Fee") &&
                      t.transferFeeFor === transaction.id
                    )
                  : transaction.type === "expense" && !isFeeTransaction
                    ? transactions.find(t =>
                        t.type === "expense" &&
                        ((t.title || "") === "Expense Fee" || (t.category || "") === "Expense Fee") &&
                        (t as any).expenseFeeFor === transaction.id
                      )
                    : null;

                // Desktop: type column (chips only, no icons)
                const getTypeDisplayDesktop = () => {
                  if (transaction.type === "adjustment") {
                    return <Chip label="Adjustment" size="small" variant="outlined" color="info" />;
                  }
                  if (transaction.type === "transfer") {
                    return <Chip label="Transfer" size="small" variant="outlined" />;
                  }
                  return (
                    <Chip
                      label={transaction.isIncome ? "Income" : "Expense"}
                      size="small"
                      color={transaction.isIncome ? "success" : "error"}
                      variant="outlined"
                    />
                  );
                };

                const getDescription = () => {
                  if (transaction.type === "adjustment") {
                    if (transaction.isInitialBalance) {
                      return "Initial Balance";
                    } else {
                      const account = getAccountById(transaction.accountId);
                      const accountCurrency = account?.currency || defaultCurrency;
                      const adjustmentAmount = transaction.amount || 0;
                      const previousAmount = transaction.previousAmount;
                      
                      if (previousAmount !== undefined && previousAmount !== null) {
                        const newAmount = previousAmount + adjustmentAmount;
                        const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
                        const formattedPrevious = formatCurrency(previousAmount, accountCurrency, accountDecimalPlaces);
                        const formattedCurrent = formatCurrency(newAmount, accountCurrency, accountDecimalPlaces);
                        return `Balance changed from ${formattedPrevious} to ${formattedCurrent}`;
                      } else {
                        const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
                        const formattedAmount = formatCurrency(adjustmentAmount, accountCurrency, accountDecimalPlaces);
                        const direction = adjustmentAmount >= 0 ? "increased" : "decreased";
                        return `Balance ${direction} to ${formattedAmount}`;
                      }
                    }
                  } else if (transaction.type === "transfer") {
                    return transaction.note || "-";
                  } else {
                    return transaction.note || "";
                  }
                };

                const getCategoryDisplay = () => {
                  if (transaction.type === "transfer" || transaction.type === "adjustment") {
                    return "-";
                  }
                  return transaction.category || "-";
                };

                const getAccountDisplay = () => {
                  if (transaction.type === "transfer") {
                    const fromAccount = getAccountById(transaction.fromAccountId);
                    const toAccount = getAccountById(transaction.toAccountId);
                    return (
                      <Box>
                        <Typography variant="body2">
                          {getAccountName(transaction.fromAccountId, transaction.fromAccountName)} → {getAccountName(transaction.toAccountId, transaction.toAccountName)}
                        </Typography>
                        {(() => {
                          const currenciesDiffer = fromAccount && toAccount && fromAccount.currency !== toAccount.currency;
                          if (currenciesDiffer && transaction.transferRate) {
                            const convertedAmount = (transaction.amount || 0) * transaction.transferRate;
                            const fromDecimalPlaces = getCurrencyDecimalPlaces(fromAccount?.currency || "", currencies, defaultCurrency);
                            const toDecimalPlaces = getCurrencyDecimalPlaces(toAccount?.currency || "", currencies, defaultCurrency);
                            return (
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(transaction.amount || 0, fromAccount?.currency || "", fromDecimalPlaces)} → {formatCurrency(convertedAmount, toAccount?.currency || "", toDecimalPlaces)} (Rate: {formatRate(transaction.transferRate)})
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    );
                  } else {
                    const accountName = getAccountName(transaction.accountId, transaction.accountName);
                    return (
                      <Box>
                        <Typography variant="body2">
                          {accountName}
                        </Typography>
                        {(() => {
                          const account = getAccountById(transaction.accountId);
                          const accountCurrency = account?.currency || defaultCurrency;
                          const expenseCurrency = transaction.currency || accountCurrency;
                          const currenciesDiffer = expenseCurrency !== accountCurrency;
                          
                          if (currenciesDiffer && transaction.rate) {
                            const amountInAccountCurrency = (transaction.amount || 0) * transaction.rate;
                            const expenseDecimalPlaces = getCurrencyDecimalPlaces(expenseCurrency, currencies, defaultCurrency);
                            const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
                            return (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatCurrency(transaction.amount || 0, expenseCurrency, expenseDecimalPlaces)} → {formatCurrency(amountInAccountCurrency, accountCurrency, accountDecimalPlaces)} (Rate: {formatRate(transaction.rate)})
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    );
                  }
                };

                const getAmountDisplay = () => {
                  if (transaction.type === "adjustment") {
                    const absAmount = Math.abs(transaction.amount || 0);
                    const sign = transaction.amount >= 0 ? "+" : "-";
                    const adjustmentDecimalPlaces = transaction.currency
                      ? getCurrencyDecimalPlaces(transaction.currency, currencies, defaultCurrency)
                      : getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                    const formatted = transaction.currency
                      ? formatCurrency(absAmount, transaction.currency, adjustmentDecimalPlaces)
                      : (() => {
                          const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                          return formatCurrency(absAmount, defaultCurrency, defaultDecimalPlaces);
                        })();
                    return (
                      <Typography
                        variant="body2"
                        fontWeight="semibold"
                        color={transaction.amount >= 0 ? "success.main" : "error.main"}
                      >
                        {sign}{formatted}
                      </Typography>
                    );
                  } else if (transaction.type === "transfer") {
                    const fromAccount = getAccountById(transaction.fromAccountId);
                    const toAccount = getAccountById(transaction.toAccountId);
                    const currenciesDiffer = fromAccount && toAccount && fromAccount.currency !== toAccount.currency;
                    
                    if (currenciesDiffer && transaction.transferRate) {
                      const convertedAmount = (transaction.amount || 0) * transaction.transferRate;
                      const fromDecimalPlaces = getCurrencyDecimalPlaces(fromAccount?.currency || "", currencies, defaultCurrency);
                      const toDecimalPlaces = getCurrencyDecimalPlaces(toAccount?.currency || "", currencies, defaultCurrency);
                      return (
                        <Box>
                          <Typography variant="body2" fontWeight="semibold" color="text.primary">
                            {formatCurrency(transaction.amount || 0, fromAccount?.currency || "", fromDecimalPlaces)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(convertedAmount, toAccount?.currency || "", toDecimalPlaces)}
                          </Typography>
                        </Box>
                      );
                    }
                    
                    const transferCurrency = transaction.currency || defaultCurrency;
                    const transferDecimalPlaces = getCurrencyDecimalPlaces(transferCurrency, currencies, defaultCurrency);
                    return (
                      <Typography variant="body2" fontWeight="semibold" color="text.primary">
                        {transaction.currency
                          ? formatCurrency(transaction.amount || 0, transaction.currency, transferDecimalPlaces)
                          : (() => {
                              const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                              return formatCurrency(transaction.amount || 0, defaultCurrency, defaultDecimalPlaces);
                            })()}
                      </Typography>
                    );
                  } else {
                    const transactionCurrency = transaction.currency || defaultCurrency;
                    const currenciesDiffer = transactionCurrency !== defaultCurrency;
                    
                    const transactionDecimalPlaces = transaction.currency
                      ? getCurrencyDecimalPlaces(transaction.currency, currencies, defaultCurrency)
                      : getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                    const formatted = transaction.currency
                      ? formatCurrency(transaction.amount || 0, transaction.currency, transactionDecimalPlaces)
                      : (() => {
                          const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                          return formatCurrency(transaction.amount || 0, defaultCurrency, defaultDecimalPlaces);
                        })();
                    
                    if (currenciesDiffer && transaction.amount) {
                      let amountInDefaultCurrency: number;
                      const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                      const storedRateRaw = transaction.defaultCurrencyRate;
                      const storedRateNum = storedRateRaw !== undefined && storedRateRaw !== null
                        ? (typeof storedRateRaw === "number" ? storedRateRaw : parseFloat(String(storedRateRaw)))
                        : NaN;
                      const hasStoredRate = !isNaN(storedRateNum) && storedRateNum > 0;
                      if (hasStoredRate) {
                        amountInDefaultCurrency = (transaction.amount || 0) * storedRateNum;
                      } else if (transaction.amountInDefaultCurrency !== undefined && 
                          transaction.defaultCurrencyAtTime === defaultCurrency) {
                        amountInDefaultCurrency = transaction.amountInDefaultCurrency;
                      } else if (transaction.defaultCurrencyAtTime && transaction.defaultCurrencyAtTime !== defaultCurrency) {
                        // Default currency changed - recalculate from transaction amount × current rate (so 1000 AMD × rate = correct default-currency amount)
                        const rateToDefault = calculateTransferRate(transactionCurrency, defaultCurrency);
                        amountInDefaultCurrency = (transaction.amount || 0) * rateToDefault;
                      } else {
                        // No historical data - calculate using current rates
                        const rateToDefault = calculateTransferRate(transactionCurrency, defaultCurrency);
                        amountInDefaultCurrency = (transaction.amount || 0) * rateToDefault;
                      }
                      
                      return (
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight="semibold"
                            color={transaction.isIncome ? "success.main" : "error.main"}
                          >
                            {transaction.isIncome ? "+" : "-"}{formatted}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(amountInDefaultCurrency, defaultCurrency, defaultDecimalPlaces)}
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return (
                      <Typography
                        variant="body2"
                        fontWeight="semibold"
                        color={transaction.isIncome ? "success.main" : "error.main"}
                      >
                        {transaction.isIncome ? "+" : "-"}{formatted}
                      </Typography>
                    );
                  }
                };

                return (
                  <React.Fragment key={`${transaction.id}-${index}`}>
                    <TableRow
                      data-transaction-id={transaction.id}
                      sx={{
                        "&:hover": { 
                          "& td": {
                            bgcolor: "action.hover",
                          }
                        },
                        cursor: "pointer",
                        "& td": {
                          border: "none",
                          bgcolor: "background.paper",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          "&:first-of-type": {
                            borderTopLeftRadius: 8,
                            borderBottomLeftRadius: 8,
                            boxShadow: "2px 0 4px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.1)",
                          },
                          "&:last-of-type": {
                            borderTopRightRadius: 8,
                            borderBottomRightRadius: 8,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.1)",
                          },
                        },
                        "&:hover td": {
                          boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      {!isMobile && (
                        <TableCell sx={{ minWidth: 72, maxWidth: 100, verticalAlign: "middle" }}>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              "& .copy-id-btn": { opacity: 0, transition: "opacity 0.15s ease" },
                              "&:hover .copy-id-btn, &:focus-within .copy-id-btn": { opacity: 1 },
                            }}
                          >
                            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexWrap: "nowrap" }}>
                              <Tooltip title={(getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence) ?? transaction.id) || "—"}>
                                <Typography variant="body2" noWrap component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", minWidth: 0 }}>
                                  {truncateIdForDisplay(getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence) ?? transaction.id ?? undefined) || "—"}
                                </Typography>
                              </Tooltip>
                              {(getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence) || transaction.id) && (
                                <CopyFeedbackTooltip
                                  copyLabel="Copy ID"
                                  feedbackKey={`desktop-id-${transaction.id}`}
                                  lastCopiedKey={lastCopiedKey}
                                  isMobile={isMobile}
                                >
                                  <IconButton
                                    className="copy-id-btn"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const id = getTransactionFriendlyId(transactionIdPrefix ?? "", transaction.friendlyIdSequence) ?? transaction.id;
                                      if (id) copyToClipboard(id).then((ok) => ok && showCopiedFeedback(`desktop-id-${transaction.id}`));
                                    }}
                                    sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                    aria-label="Copy transaction ID"
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </CopyFeedbackTooltip>
                              )}
                            </Stack>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "nowrap", minWidth: "fit-content" }}>
                          <Typography variant="body2" noWrap sx={{ flexShrink: 0 }}>
                            {formatDate(transaction.date)}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ flexShrink: 0, opacity: 0.7, ml: 0.5 }}>
                            {formatTime(transaction.date)}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, gap: 0.25, minWidth: 24 }}>
                            {transactionHasHistory.has(transaction.id) && (
                              <Tooltip title="This transaction has been edited">
                                <EditIcon sx={{ fontSize: 16, color: "primary.main" }} />
                              </Tooltip>
                            )}
                            {/* Connection indicators */}
                            {isFeeTransaction && relatedTransfer && (
                              <Tooltip title={`Fee for transfer: ${formatCurrency(relatedTransfer.amount || 0, relatedTransfer.currency || defaultCurrency)}`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const transferElement = document.querySelector(`[data-transaction-id="${relatedTransfer.id}"]`);
                                    if (transferElement) {
                                      transferElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                      // Smooth highlight animation
                                      setTimeout(() => {
                                        const row = transferElement.closest("tr");
                                        if (row) {
                                          const rowElement = row as HTMLElement;
                                          const cells = row.querySelectorAll("td");
                                          
                                          // Set smooth transitions
                                          rowElement.style.transition = "background-color 0.5s ease-in-out";
                                          cells.forEach((cell) => {
                                            (cell as HTMLElement).style.transition = "background-color 0.5s ease-in-out";
                                          });
                                          
                                          // Fade in highlight
                                          rowElement.style.backgroundColor = "rgba(144, 202, 249, 0.4)";
                                          cells.forEach((cell) => {
                                            (cell as HTMLElement).style.backgroundColor = "rgba(144, 202, 249, 0.4)";
                                          });
                                          
                                          // Smooth fade out after 2.5 seconds
                                          setTimeout(() => {
                                            rowElement.style.transition = "background-color 1s ease-in-out";
                                            cells.forEach((cell) => {
                                              (cell as HTMLElement).style.transition = "background-color 1s ease-in-out";
                                            });
                                            rowElement.style.backgroundColor = "";
                                            cells.forEach((cell) => {
                                              (cell as HTMLElement).style.backgroundColor = "";
                                            });
                                          }, 2500);
                                        }
                                      }, 100);
                                    }
                                  }}
                                  sx={{ 
                                    p: 0.25,
                                    "&:hover": { bgcolor: "action.hover" },
                                  }}
                                >
                                  <LinkIcon sx={{ fontSize: 14, color: "primary.main" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isFeeTransaction && relatedExpense && (
                              <Tooltip title={`Fee for expense: ${(relatedExpense.title || relatedExpense.category || "").slice(0, 30)}`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const expenseElement = document.querySelector(`[data-transaction-id="${relatedExpense.id}"]`);
                                    if (expenseElement) {
                                      expenseElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                      setTimeout(() => {
                                        const row = expenseElement.closest("tr");
                                        if (row) {
                                          const rowElement = row as HTMLElement;
                                          const cells = row.querySelectorAll("td");
                                          rowElement.style.transition = "background-color 0.5s ease-in-out";
                                          cells.forEach((cell) => { (cell as HTMLElement).style.transition = "background-color 0.5s ease-in-out"; });
                                          rowElement.style.backgroundColor = "rgba(144, 202, 249, 0.4)";
                                          cells.forEach((cell) => { (cell as HTMLElement).style.backgroundColor = "rgba(144, 202, 249, 0.4)"; });
                                          setTimeout(() => {
                                            rowElement.style.transition = "background-color 1s ease-in-out";
                                            cells.forEach((cell) => { (cell as HTMLElement).style.transition = "background-color 1s ease-in-out"; });
                                            rowElement.style.backgroundColor = "";
                                            cells.forEach((cell) => { (cell as HTMLElement).style.backgroundColor = ""; });
                                          }, 2500);
                                        }
                                      }, 100);
                                    }
                                  }}
                                  sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                >
                                  <LinkIcon sx={{ fontSize: 14, color: "primary.main" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(transaction.type === "transfer" || transaction.type === "expense") && relatedFee && (
                              <Tooltip title={`Has fee: ${formatCurrency(relatedFee.amount || 0, relatedFee.currency || defaultCurrency)}`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const feeElement = document.querySelector(`[data-transaction-id="${relatedFee.id}"]`);
                                    if (feeElement) {
                                      feeElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                      // Smooth highlight animation
                                      setTimeout(() => {
                                        const row = feeElement.closest("tr");
                                        if (row) {
                                          const rowElement = row as HTMLElement;
                                          const cells = row.querySelectorAll("td");
                                          
                                          // Set smooth transitions
                                          rowElement.style.transition = "background-color 0.5s ease-in-out";
                                          cells.forEach((cell) => {
                                            (cell as HTMLElement).style.transition = "background-color 0.5s ease-in-out";
                                          });
                                          
                                          // Fade in highlight
                                          rowElement.style.backgroundColor = "rgba(144, 202, 249, 0.4)";
                                          cells.forEach((cell) => {
                                            (cell as HTMLElement).style.backgroundColor = "rgba(144, 202, 249, 0.4)";
                                          });
                                          
                                          // Smooth fade out after 2.5 seconds
                                          setTimeout(() => {
                                            rowElement.style.transition = "background-color 1s ease-in-out";
                                            cells.forEach((cell) => {
                                              (cell as HTMLElement).style.transition = "background-color 1s ease-in-out";
                                            });
                                            rowElement.style.backgroundColor = "";
                                            cells.forEach((cell) => {
                                              (cell as HTMLElement).style.backgroundColor = "";
                                            });
                                          }, 2500);
                                        }
                                      }, 100);
                                    }
                                  }}
                                  sx={{ 
                                    p: 0.25,
                                    "&:hover": { bgcolor: "action.hover" },
                                  }}
                                >
                                  <AccountBalanceWalletIcon sx={{ fontSize: 14, color: "warning.main" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{getTypeDisplayDesktop()}</TableCell>
                      <TableCell sx={{ maxWidth: 120, width: 120 }}>
                        <Typography variant="body2" noWrap>{getCategoryDisplay()}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200, maxWidth: 300 }}>
                        <TruncatedTextTooltip text={getDescription() || ""} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        {(() => {
                          if (transaction.type === "transfer") {
                            return "-";
                          }
                          const accountGroup = getAccountGroup(transaction.accountId);
                          return (
                            <Typography variant="body2" noWrap>
                              {accountGroup || "-"}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>{getAccountDisplay()}</TableCell>
                      <TableCell>
                        {transaction.userId ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              src={getUserPhotoURL(transaction.userId) || undefined}
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "primary.main",
                                fontSize: "0.875rem",
                              }}
                            >
                              {getUserName(transaction.userId)[0]?.toUpperCase() || "?"}
                            </Avatar>
                            <Typography variant="body2" noWrap>
                              {getUserName(transaction.userId)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 88 }}>
                        {(() => {
                          const attachments = getTransactionAttachments(transaction);
                          if (attachments.length === 0) {
                            return (
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <AttachFileIcon sx={{ fontSize: 16, color: "text.disabled", opacity: 0.3 }} />
                              </Box>
                            );
                          }
                          return (
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
                              {attachments.map((att, i) => (
                                <Tooltip
                                  key={i}
                                  title={
                                    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                                      {att.label ? `${att.label}: ${att.name}` : att.name}
                                    </Typography>
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAttachment(att.url, usePreviewDialog ? { forPreview: true, name: att.name, type: att.type } : undefined);
                                    }}
                                    sx={{ p: 0.5 }}
                                    color="primary"
                                  >
                                    {getAttachmentIcon(att.type, att.name)}
                                  </IconButton>
                                </Tooltip>
                              ))}
                            </Stack>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">{getAmountDisplay()}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          {(() => {
                            const hasDetails = !!(transaction.details && Object.keys(transaction.details).length > 0);
                            const hasCustomFields = !!(transaction.customFieldValues && Object.keys(transaction.customFieldValues).length > 0 && transactionCustomFields.length > 0);
                            const canExpand = hasDetails || hasCustomFields;
                            return (
                              <Tooltip title={canExpand ? "Show details" : "No details available"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedDetails((prev) => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(transaction.id)) {
                                          newSet.delete(transaction.id);
                                        } else {
                                          newSet.add(transaction.id);
                                        }
                                        return newSet;
                                      });
                                    }}
                                    disabled={!canExpand}
                                  >
                                {expandedDetails.has(transaction.id) ? (
                                    <ExpandMoreIcon fontSize="small" />
                                  ) : (
                                    <ChevronRightIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                          })()}
                          <Tooltip 
                            title={
                              isProtectedAdjustment(transaction)
                                ? "This adjustment transaction cannot be edited"
                                : usesArchivedAccount(transaction)
                                ? `The account "${getArchivedAccountNames(transaction).join('", "')}" used for this transaction is currently archived, so this transaction cannot be edited.`
                                : ""
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMenuOpen(e, transaction);
                                }}
                                disabled={isProtectedAdjustment(transaction) || usesArchivedAccount(transaction)}
                                sx={{ 
                                  zIndex: 1, // Ensure button is clickable
                                  position: "relative",
                                }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {/* Single expanded row: details + custom fields inside transaction (like mobile) */}
                    {expandedDetails.has(transaction.id) && (!!(transaction.details && Object.keys(transaction.details).length > 0) || !!(transaction.customFieldValues && Object.keys(transaction.customFieldValues).length > 0 && transactionCustomFields.length > 0)) && (
                      <TableRow key={`${transaction.id}-${index}-expanded`} sx={{ "& td": { borderTop: "none", pt: 0 } }}>
                        <TableCell colSpan={isMobile ? 10 : 11} sx={{ py: 0, pt: 0, pb: 1, verticalAlign: "top", borderBottom: "none" }}>
                          <Box sx={{ pl: 2, pr: 2, pt: 1, pb: 1, bgcolor: "action.hover", borderRadius: 1, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                            {transaction.details && Object.keys(transaction.details).length > 0 && (
                              <Box sx={{ mb: transaction.customFieldValues && Object.keys(transaction.customFieldValues).length > 0 && transactionCustomFields.length > 0 ? 1.5 : 0 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block", fontWeight: "medium" }}>
                                  Transaction Details:
                                </Typography>
                                <Stack spacing={0.5}>
                                  {getDetailOrder(transaction).map((key) => {
                                    const value = transaction.details![key];
                                    if (value === undefined) return null;
                                    const unit = getDetailUnit(transaction, key);
                                    const displayValue = typeof value === "number" ? value.toString() : value || "-";
                                    return (
                                      <Stack key={key} direction="row" spacing={1} alignItems="flex-start">
                                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, fontWeight: "medium" }}>
                                          {key}:
                                        </Typography>
                                        <Typography variant="body2">
                                          {displayValue}{unit ? ` ${unit}` : ""}
                                        </Typography>
                                      </Stack>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            )}
                            {transaction.customFieldValues && Object.keys(transaction.customFieldValues).length > 0 && transactionCustomFields.length > 0 && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block", fontWeight: "medium" }}>
                                  Custom fields:
                                </Typography>
                                <Stack spacing={0.5}>
                                  {transactionCustomFields
                                    .filter((f) => transaction.customFieldValues?.[f.id] != null)
                                    .map((field) => {
                                    const value = transaction.customFieldValues![field.id];
                                    const label = field.label;
                                    const isAttachment = value && typeof value === "object" && "url" in value;
                                    const attachmentVal = isAttachment ? (value as CustomFieldAttachmentValue) : null;
                                    const displayValue = typeof value === "string" ? value : (value && typeof value === "object" && "name" in value ? (value as { name?: string }).name : "-");
                                    if (displayValue === undefined || displayValue === "") return null;
                                    const textToCopy = typeof displayValue === "string" ? displayValue : "";
                                    return (
                                      <Stack key={field.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        {attachmentVal?.url ? (
                                          <Tooltip title={usePreviewDialog ? "Preview" : "Download"}>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openAttachment(attachmentVal.url, usePreviewDialog ? { forPreview: true, name: attachmentVal.name, type: attachmentVal.type } : undefined); }} sx={{ p: 0.25 }} color="primary">
                                              <DownloadIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : (
                                          <CopyFeedbackTooltip
                                            copyLabel="Copy"
                                            feedbackKey={`desktop-field-${transaction.id}-${field.id}`}
                                            lastCopiedKey={lastCopiedKey}
                                            isMobile={isMobile}
                                          >
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyToClipboard(textToCopy).then((ok) => ok && showCopiedFeedback(`desktop-field-${transaction.id}-${field.id}`)); }} sx={{ p: 0.25 }} color="inherit">
                                              <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                          </CopyFeedbackTooltip>
                                        )}
                                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, fontWeight: "medium" }}>
                                          {label}:
                                        </Typography>
                                        {attachmentVal?.url ? (
                                          <>
                                            <Tooltip title={attachmentVal.name || (usePreviewDialog ? "Preview" : "Open")}>
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openAttachment(attachmentVal.url, usePreviewDialog ? { forPreview: true, name: attachmentVal.name, type: attachmentVal.type } : undefined);
                                                }}
                                                sx={{ p: 0.5 }}
                                                color="primary"
                                              >
                                                {getAttachmentIcon(attachmentVal.type, attachmentVal.name)}
                                              </IconButton>
                                            </Tooltip>
                                            <Typography variant="body2" sx={{ cursor: "pointer", textDecoration: "underline" }} onClick={(e) => { e.stopPropagation(); openAttachment(attachmentVal.url, usePreviewDialog ? { forPreview: true, name: attachmentVal.name, type: attachmentVal.type } : undefined); }}>
                                              {attachmentVal.name}
                                            </Typography>
                                          </>
                                        ) : (
                                          <Typography variant="body2">
                                            {displayValue}
                                          </Typography>
                                        )}
                                      </Stack>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {displayedCount < filteredTransactions.length && (
                <TableRow key="showing-count">
                  <TableCell colSpan={isMobile ? 10 : 11} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {displayedCount} of {filteredTransactions.length} transactions
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {displayedCount >= filteredTransactions.length && hasMoreTransactions && (
                <TableRow key="load-more">
                  <TableCell colSpan={isMobile ? 10 : 11} align="center" sx={{ py: 2 }}>
                    {loadingMore ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Scroll to load more transactions...
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
              {displayedCount >= filteredTransactions.length && !hasMoreTransactions && filteredTransactions.length > 0 && (
                <TableRow key="all-shown">
                  <TableCell colSpan={isMobile ? 10 : 11} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      You've seen all {filteredTransactions.length} transactions
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
